import React from "react";
import { generatePath } from "react-router-dom";
import { Info } from "lucide-react";
import { CAMPAIGN_ENUM_TYPES, CAMPAIGN_SUBTYPES_ENUM, DMDocumentFormats } from "../shared/constants";
import { createSSCampaign } from "../graphQL";
import { EDDM_ROUTING } from "../config/routing";
import { getMinimumCampaignValue } from "./minimumCampaignValue";

interface MinimumCampaignModalParams {
  countryName: string | undefined;
  cityName: string | undefined;
  clientId: string | number;
  history: { push: (path: string) => void };
  onError?: (msg: string) => void;
}

export const getMinimumCampaignModalConfig = ({
  countryName,
  cityName,
  clientId,
  history,
  onError,
}: MinimumCampaignModalParams) => {
  const minimum = getMinimumCampaignValue(countryName, cityName);
  if (!minimum) return null;

  // EDDM is only offered in the US. For other countries (e.g. Canada) show a
  // simplified acknowledge-only modal: title + message and a single white
  // "Got it" button that just closes it — no EDDM cross-sell, no primary CTA.
  if (countryName !== "United States") {
    return {
      title: `Select at least ${minimum.formatted} to continue`,
      contentText: (
        <div style={{ color: "#626379", fontSize: "16px" }}>
          Flyering campaigns require a minimum investment of {minimum.formatted}
        </div>
      ),
      hasOptions: false,
      ctaLabel: "Got it",
      customCtaStyles: {
        width: "262px",
        borderRadius: "12px",
        padding: "10px 0",
        border: "1px solid #1A2547",
        color: "#1A2547",
        background: "#fff",
      },
      handleCTAClick: () => {},
    };
  }

  return {
    title: `Select at least ${minimum.formatted} to continue`,
    contentText: (
      <div>
        <div style={{ color: "#626379", fontSize: "16px", marginBottom: "16px" }}>
          Flyering campaigns require a minimum investment of {minimum.formatted}
        </div>
        <div
          style={{
            background: "#F6F6FF",
            border: "1px solid #E2E2FC",
            borderRadius: "8px",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "16px",
            color: "#222336",
          }}
        >
          <Info size={20} color="#6E6E8D" style={{ flexShrink: 0 }} />
          <span style={{ textAlign: "left" }}>
            Want to start smaller? <strong>EDDM</strong> (Every Door Direct Mail) starts at $1,000.
          </span>
        </div>
      </div>
    ),
    hasOptions: true,
    secondaryButtonLabel: "Switch to EDDM",
    ctaLabel: "Continue with Flyering",
    customCtaStyles: { width: "262px", borderRadius: "12px", padding: "10px 0" },
    customSecondaryStyles: {
      width: "262px",
      borderRadius: "12px",
      padding: "10px 0",
      border: "1px solid #1A2547",
      color: "#1A2547",
    },
    handleSecondaryButtonClick: async () => {
      const MAX_RETRIES = 10;
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const name = attempt === 1 ? "EDDM Campaign" : `EDDM Campaign ${attempt}`;
          const response = await createSSCampaign({
            name,
            type: CAMPAIGN_ENUM_TYPES.DIRECTMAIL,
            subtype: CAMPAIGN_SUBTYPES_ENUM.EDDM,
            flyerType: DMDocumentFormats.BEST_SELLER,
            flyerWeight: null,
            baPerLocation: null,
            lastActiveStep: "home",
          });
          const result = await (response as Response).json();
          if (result.errors) {
            const isDuplicate = result.errors.some((e: { message?: string }) =>
              e.message?.includes("IDX_UNIQUE_CAMPAIGN_CLIENT")
            );
            if (isDuplicate && attempt < MAX_RETRIES) continue;
            throw new Error(result.errors[0]?.message || "Failed to create campaign");
          }
          const {
            data: {
              createSSCampaign: { campaignId: newCampaignId },
            },
          } = result;
          history.push(
            generatePath(EDDM_ROUTING.EDDM_CAMPAIGN_SEGMENTS, {
              campaignId: newCampaignId,
              clientId: String(clientId),
            })
          );
          return;
        } catch (err) {
          if (attempt === MAX_RETRIES) {
            console.error("Failed to create EDDM campaign:", err);
            if (onError) {
              onError("Failed to create EDDM campaign. Please try again.");
            }
          }
        }
      }
    },
    handleCTAClick: () => {},
  };
};
